import { useEffect, useMemo, useState } from "react";
import {
  Brand,
  Button,
  Card,
  CardContent,
  Flex,
  Icon,
  Icons,
  Menu,
  Paragraph,
  Spin,
  Switch,
  useToast,
} from "@ir/ui";
import type { OrganizationsBrandsLookupItem } from "@src/domain/dto/organizationsBrands";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { buildOrgBrandMenuItems } from "@src/presentation/pages/UsersAccess/utils/buildOrgBrandMenuItems";
import { CreateBrandPurchaserAssociationDTO } from "@src/domain/dto/purchasers";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";

interface AssociateBrandRowProps {
  purchaserId: number;
  assignedBrandIds: ReadonlySet<number>;
  isSaving: boolean;
  onCancel: () => void;
  onSaveStart: () => void;
  onSaveEnd: () => void;
}

function filterAssignableBrands(
  organizations: OrganizationsBrandsLookupItem[],
  assignedBrandIds: ReadonlySet<number>,
): OrganizationsBrandsLookupItem[] {
  return organizations
    .filter((organization) => organization.organizationIsActive)
    .map((organization) => ({
      ...organization,
      brands: (organization.brands ?? []).filter(
        (brand) => brand.brandIsActive && !assignedBrandIds.has(brand.brandId),
      ),
    }))
    .filter((organization) => (organization.brands?.length ?? 0) > 0)
    .sort((a, b) =>
      (a.organizationDisplayName ?? a.organizationName).localeCompare(
        b.organizationDisplayName ?? b.organizationName,
      ),
    );
}

function findBrandByKey(
  organizations: OrganizationsBrandsLookupItem[],
  brandKey: string,
) {
  for (const organization of organizations) {
    const brand = (organization.brands ?? []).find(
      (item) => item.brandKey === brandKey,
    );
    if (brand) {
      return {
        brand,
        organizationName:
          organization.organizationDisplayName ?? organization.organizationName,
      };
    }
  }
  return null;
}

export const AssociateBrandRow = ({
  purchaserId,
  assignedBrandIds,
  isSaving,
  onCancel,
  onSaveStart,
  onSaveEnd,
}: AssociateBrandRowProps) => {
  const {
    organizationsBrandsLookup,
    organizationsBrandsLookupLoading,
    organizationsBrandsLookupError,
  } = useGlobalDataLookup();
  const { createBrandPurchaserAssociation, applyBrandAssociationUpdate } =
    usePurchasersData();
  const { success: successToast, error: errorToast } = useToast();
  const [selectedBrandKey, setSelectedBrandKey] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const assignableOrganizations = useMemo(
    () =>
      filterAssignableBrands(organizationsBrandsLookup ?? [], assignedBrandIds),
    [organizationsBrandsLookup, assignedBrandIds],
  );

  const menuItems = useMemo(
    () => buildOrgBrandMenuItems(assignableOrganizations),
    [assignableOrganizations],
  );

  const selectedBrand = useMemo(
    () =>
      selectedBrandKey
        ? findBrandByKey(assignableOrganizations, selectedBrandKey)
        : null,
    [assignableOrganizations, selectedBrandKey],
  );

  useEffect(() => {
    if (organizationsBrandsLookupError) {
      errorToast({ content: organizationsBrandsLookupError });
    }
  }, [errorToast, organizationsBrandsLookupError]);

  const handleMenuSelect = (key: string) => {
    if (findBrandByKey(assignableOrganizations, key)) {
      setSelectedBrandKey(key);
    }
  };

  const handleSave = async () => {
    if (!selectedBrand) return;
    onSaveStart();
    setIsSubmitting(true);
    try {
      const validated = CreateBrandPurchaserAssociationDTO.validate({
        purchaserId,
        brandId: selectedBrand.brand.brandId,
        isActive,
      });
      if (!validated.success) {
        throw new Error("Validation failed");
      }

      const association = await createBrandPurchaserAssociation(
        new CreateBrandPurchaserAssociationDTO(validated.data),
      );
      applyBrandAssociationUpdate(purchaserId, association);
      successToast({
        content: "Brand associated successfully.",
        icon: <Icon name={Icons.CheckCircle} />,
      });
      onCancel();
    } catch (error) {
      console.error("Failed to associate brand", error);
      errorToast({
        content: "Failed to update. Please try again.",
        icon: <Icon name={Icons.AlertCircle} />,
      });
    } finally {
      setIsSubmitting(false);
      onSaveEnd();
    }
  };

  const saving = isSaving || isSubmitting;
  const selectedBrandLabel =
    selectedBrand?.brand.brandDisplayName ?? selectedBrand?.brand.brandName;

  let brandMenuContent = (
    <Flex align="center" gap={1} style={{ minWidth: 200 }}>
      <Icon name={Icons.Organization} />
      <Paragraph className="m-0 text-grey-500 font-bold">
        Select Brand
      </Paragraph>
    </Flex>
  );

  if (organizationsBrandsLookupLoading) {
    brandMenuContent = <Spin size="small" />;
  } else if (selectedBrand) {
    brandMenuContent = (
      <>
        <Brand
          size="small"
          variant="compact"
          brandName={selectedBrandLabel ?? ""}
          logoSrcCompact={selectedBrand.brand.brandLogoUrl?.trim() || undefined}
        />
        <Flex direction="column" style={{ minWidth: 200, textAlign: "left" }}>
          <Paragraph className="m-0 text-grey-700 font-bold text-truncate">
            {selectedBrandLabel}
          </Paragraph>
        </Flex>
      </>
    );
  }

  return (
    <Card className="mb-2 w-100">
      <CardContent className="bg-white shadow-sm border-1 border-color-grey-25 rounded-medium">
        <Flex align="center" justifyContent="space-between">
          <Flex
            direction="column"
            className="me-2 flex-1"
            style={{ minWidth: 0 }}
          >
            <Menu
              items={menuItems}
              onSelect={handleMenuSelect}
              aria-label="Select organization and brand"
              placement="top start"
              style={{ height: "200px !important", overflowY: "auto" }}
            >
              <Button
                variant="clear"
                size="medium"
                color="grey"
                className="w-50 border-1 border-color-grey-25 bg-white"
                isDisabled={saving || organizationsBrandsLookupLoading}
                style={{ justifyContent: "space-between", maxWidth: 280 }}
              >
                <Flex align="center" gap={0.5} style={{ minWidth: 0 }}>
                  {brandMenuContent}
                </Flex>
                <Icon name={Icons.ChevronDown} />
              </Button>
            </Menu>
          </Flex>
          <Flex align="center" gap={0.5}>
            <Switch
              size="small"
              isSelected={isActive}
              onChange={setIsActive}
              isDisabled={saving}
              aria-label="Brand association active"
              role="switch"
              htmlTitle="Brand association active"
              className="ms-1"
            />

            <Button
              size="small"
              color="primary"
              variant="clear"
              onPress={handleSave}
              isDisabled={saving || !selectedBrand}
              isLoading={saving}
              aria-label="Save brand association"
            >
              <Icon name={Icons.Check} />
            </Button>
            <Button
              size="small"
              color="grey"
              variant="clear"
              onPress={onCancel}
              isDisabled={saving}
              aria-label="Cancel add brand"
              style={{ marginLeft: "0" }}
            >
              <Icon name={Icons.Close} />
            </Button>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
};
